/**
 * Modal pro editaci písničky v repertoárové databázi.
 *
 * Editovatelné položky:
 *  - song_number (int, unique)
 *  - title
 *  - duration_sec (vstup m:ss nebo sekundy)
 *  - instruments JSONB — pole muzikant→nástroj + DRUM checkbox
 *  - notes
 *
 * Otevřít: openSongEditModal(songId, onSaved)
 */
import { supabase } from '../lib/supabase.js';

// Známí muzikanti — datalist návrhy. Volné texty zůstávají povoleny.
const KNOWN_MUSICIANS = [
    { key: 'michal', label: 'Michal' },
    { key: 'matous', label: 'Matouš' },
    { key: 'misa',   label: 'Míša' },
    { key: 'tata',   label: 'Táta' }
];

const KNOWN_INSTRUMENTS = [
    'kytara', 'key', 'bicí', 'aku', 'tenor', 'alt', 'sopran', 'sopran/alt', 'ten/alt', 'voc'
];

const SINGER_OPTIONS = [
    { value: 'misa',    label: 'Míša',   bg: '#fff4c2', border: '#f0d770' },
    { value: 'matous',  label: 'Matouš', bg: '#d4f0d0', border: '#8fc788' },
    { value: 'michal',  label: 'Michal', bg: '#d6e4f7', border: '#7da7d9' }
];

function esc(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function fmtDuration(sec) {
    if (!sec || sec <= 0) return '';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

function parseDuration(raw) {
    const val = String(raw || '').trim();
    if (!val) return null;
    if (val.includes(':')) {
        const [m, s] = val.split(':').map((n) => parseInt(n, 10) || 0);
        return m * 60 + s;
    }
    const n = parseInt(val, 10);
    if (isNaN(n)) return null;
    return n < 60 ? n * 60 : n;
}

function buildModal() {
    let m = document.getElementById('song-edit-modal');
    if (m) m.remove();
    m = document.createElement('div');
    m.id = 'song-edit-modal';
    m.className = 'modal-overlay';
    document.body.appendChild(m);
    return m;
}

export async function openSongEditModal(songId, onSaved, opts = {}) {
    const isCreate = !songId;
    let song;
    if (isCreate) {
        // Nová písnička — prázdný formulář
        song = { song_number: null, title: '', duration_sec: null, instruments: {}, singer: null, singers: [], notes: '' };
    } else {
        const { data, error } = await supabase
            .from('songs').select('*').eq('id', songId).single();
        if (error) {
            alert('Nepodařilo se načíst písničku: ' + error.message);
            return;
        }
        song = data;
    }

    // Rozparsuj instruments na pole {key, instrument} + drum flag
    const ins = song.instruments && typeof song.instruments === 'object' ? song.instruments : {};
    const drumFlag = ins.drum === true || String(ins.drum).toUpperCase() === 'DRUM';
    const musicianRows = [];
    for (const [k, v] of Object.entries(ins)) {
        if (k === 'drum') continue;
        if (v == null || v === '' || v === false) continue;
        musicianRows.push({ key: k, instrument: String(v) });
    }

    const datalistMusicians = KNOWN_MUSICIANS.map((m) => `<option value="${esc(m.key)}">${esc(m.label)}</option>`).join('');
    const datalistInstruments = KNOWN_INSTRUMENTS.map((i) => `<option value="${esc(i)}">`).join('');

    const modal = buildModal();
    modal.innerHTML = `
        <div class="modal-window song-edit-window">
            <div class="modal-head">
                <h2>${isCreate ? 'Nová písnička' : `Upravit písničku${song.song_number ? ` č. ${song.song_number}` : ''}`}</h2>
                <button class="modal-close" id="se-close" title="Zavřít (Esc)">×</button>
            </div>
            <form class="modal-body song-edit-form" id="se-form">
                <div class="se-grid-2">
                    <label class="se-field">
                        <span>Číslo</span>
                        <input type="number" id="se-number" value="${song.song_number ?? ''}" min="0">
                    </label>
                    <label class="se-field">
                        <span>Doba (m:ss)</span>
                        <input type="text" id="se-duration" placeholder="3:30" value="${esc(fmtDuration(song.duration_sec))}">
                    </label>
                </div>
                <label class="se-field">
                    <span>Název</span>
                    <input type="text" id="se-title" value="${esc(song.title || '')}" required autofocus>
                </label>
                <label class="se-field">
                    <span>Odkaz na YouTube (nepovinné)</span>
                    <input type="url" id="se-youtube" placeholder="https://youtube.com/..." value="${esc(song.youtube_url || '')}">
                </label>

                <div class="se-field">
                    <span>Zpívá (barva v plánovači, lze duet)</span>
                    <div class="se-singers-row">
                        ${SINGER_OPTIONS.map((o) => {
                            const checked = (Array.isArray(song.singers) && song.singers.includes(o.value))
                                || (!song.singers?.length && song.singer === o.value);
                            return `
                                <label class="se-singer-chip" style="background:${o.bg}; border-color:${o.border};">
                                    <input type="checkbox" class="se-singer-cb" value="${esc(o.value)}" ${checked ? 'checked' : ''}>
                                    <span>${esc(o.label)}</span>
                                </label>
                            `;
                        }).join('')}
                    </div>
                </div>

                <div class="se-section">
                    <div class="se-section-head">
                        <h3>Muzikanti / nástroje</h3>
                        <button type="button" class="btn btn-sm" id="se-add-musician">+ Přidat muzikanta</button>
                    </div>
                    <div class="se-musicians" id="se-musicians"></div>
                    <datalist id="se-musician-options">${datalistMusicians}</datalist>
                    <datalist id="se-instrument-options">${datalistInstruments}</datalist>
                </div>

                <label class="se-check">
                    <input type="checkbox" id="se-drum" ${drumFlag ? 'checked' : ''}>
                    <span>Akustický drum podklad (DRUM)</span>
                </label>

                <label class="se-field">
                    <span>Poznámka</span>
                    <textarea id="se-notes" rows="2">${esc(song.notes || '')}</textarea>
                </label>

                <div class="se-error" id="se-error" hidden></div>
            </form>
            <div class="modal-foot">
                ${typeof opts.onDiscard === 'function'
                    ? `<button class="btn se-discard-btn" id="se-discard" type="button">${esc(opts.discardLabel || 'Vyřadit')}</button>`
                    : ''}
                <button class="btn" id="se-cancel">Zrušit</button>
                <button class="btn btn-primary" id="se-save">${isCreate ? 'Vytvořit' : 'Uložit'}</button>
            </div>
        </div>
    `;

    // Render existing musician rows
    const musiciansContainer = modal.querySelector('#se-musicians');
    if (musicianRows.length === 0) addMusicianRow(musiciansContainer);
    else musicianRows.forEach((r) => addMusicianRow(musiciansContainer, r.key, r.instrument));

    // Listeners
    modal.querySelector('#se-add-musician').addEventListener('click', () => addMusicianRow(musiciansContainer));

    const close = () => { modal.classList.remove('open'); modal.remove(); };
    modal.querySelector('#se-close').addEventListener('click', close);
    modal.querySelector('#se-cancel').addEventListener('click', close);

    const discardBtn = modal.querySelector('#se-discard');
    if (discardBtn) {
        const discardLabel = opts.discardLabel || 'Vyřadit';
        discardBtn.addEventListener('click', async () => {
            discardBtn.disabled = true;
            discardBtn.textContent = 'Pracuji…';
            try {
                await opts.onDiscard();
                close();
            } catch (err) {
                alert('Operace selhala: ' + (err?.message || err));
                discardBtn.disabled = false;
                discardBtn.textContent = discardLabel;
            }
        });
    }
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    document.addEventListener('keydown', function onEsc(e) {
        if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onEsc); }
        else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); save(); }
    });

    const save = async () => {
        const errBox = modal.querySelector('#se-error');
        errBox.hidden = true;
        errBox.textContent = '';

        const title = modal.querySelector('#se-title').value.trim();
        if (!title) { showError(errBox, 'Název je povinný'); return; }

        const rawNumber = modal.querySelector('#se-number').value.trim();
        const song_number = rawNumber === '' ? null : parseInt(rawNumber, 10);

        const duration_sec = parseDuration(modal.querySelector('#se-duration').value);
        const drumChecked = modal.querySelector('#se-drum').checked;
        const notes = modal.querySelector('#se-notes').value.trim();
        const singers = [...modal.querySelectorAll('.se-singer-cb:checked')].map((cb) => cb.value).sort();
        const singer = singers[0] || null;

        // Sesbírej muzikanty
        const instruments = {};
        const dupCheck = new Set();
        let hasError = false;
        modal.querySelectorAll('.se-musician-row').forEach((row) => {
            const k = row.querySelector('.se-m-key').value.trim().toLowerCase();
            const v = row.querySelector('.se-m-instr').value.trim();
            if (!k && !v) return;
            if (!k) { showError(errBox, `Chybí jméno muzikanta u nástroje "${v}"`); hasError = true; return; }
            if (!v) { showError(errBox, `Chybí nástroj u muzikanta "${k}"`); hasError = true; return; }
            if (dupCheck.has(k)) { showError(errBox, `Muzikant "${k}" je tam dvakrát`); hasError = true; return; }
            dupCheck.add(k);
            instruments[k] = v;
        });
        if (hasError) return;
        if (drumChecked) instruments.drum = true;

        const saveBtn = modal.querySelector('#se-save');
        saveBtn.disabled = true;
        saveBtn.textContent = 'Ukládám…';

        const payload = {
            song_number,
            title,
            duration_sec,
            instruments,
            singer,
            singers,
            youtube_url: (modal.querySelector('#se-youtube').value || '').trim() || null,
            notes: notes || null,
            updated_at: new Date().toISOString()
        };

        let upErr, newId;
        if (isCreate) {
            const { data, error } = await supabase.from('songs').insert(payload).select('id').single();
            upErr = error;
            newId = data?.id;
        } else {
            ({ error: upErr } = await supabase.from('songs').update(payload).eq('id', songId));
        }

        saveBtn.disabled = false;
        saveBtn.textContent = isCreate ? 'Vytvořit' : 'Uložit';

        if (upErr) {
            const friendly = upErr.code === '23505'
                ? `Číslo písničky ${song_number} už používá jiná píseň.`
                : upErr.message;
            showError(errBox, 'Uložení selhalo: ' + friendly);
            return;
        }

        close();
        if (typeof onSaved === 'function') onSaved(newId);
    };

    modal.querySelector('#se-save').addEventListener('click', save);
    modal.querySelector('#se-form').addEventListener('submit', (e) => { e.preventDefault(); save(); });

    modal.classList.add('open');
    // autofocus na název
    setTimeout(() => modal.querySelector('#se-title')?.focus(), 50);
}

function addMusicianRow(container, key = '', instrument = '') {
    const row = document.createElement('div');
    row.className = 'se-musician-row';
    row.innerHTML = `
        <input class="se-m-key" list="se-musician-options"
               placeholder="muzikant" value="${esc(key)}" autocomplete="off">
        <input class="se-m-instr" list="se-instrument-options"
               placeholder="nástroj (kytara, key, tenor…)" value="${esc(instrument)}" autocomplete="off">
        <button type="button" class="se-m-remove" title="Odebrat muzikanta">×</button>
    `;
    row.querySelector('.se-m-remove').addEventListener('click', () => row.remove());
    container.appendChild(row);
}

function showError(box, msg) {
    box.hidden = false;
    box.textContent = msg;
}
